import type { AutomationEvidence } from "../types";

/**
 * "Why not just block everything?" - answered before it is asked.
 *
 * Two of the three columns describe systems that were never built, so their cells are
 * arithmetic on the dataset (fraud rate, volume), never invented measurements. The third
 * column is the real evaluation result, and it is labelled as the only measured one.
 */

interface Props {
  evidence: AutomationEvidence;
}

export function ThreeSystems({ evidence }: Props) {
  const { rule, assumed_daily_transactions: daily, dataset_fraud_rate: fraudRate } = evidence;

  const dailyFraud = Math.round(daily * fraudRate);
  const dailyReviews = rule.projected_daily_reviews;
  const caughtByRule = Math.round(dailyFraud * rule.fraud_recall);

  return (
    <section className="card">
      <h3>💬 &quot;그냥 전부 막으면 안 되나요?&quot;</h3>
      <p className="muted">
        좋은 질문이에요! 같은 거래를 세 가지 방식으로 처리하면 무슨 일이 생기는지 비교해 봤어요.
        하루 {daily.toLocaleString()}건, 사기 비율 {(fraudRate * 100).toFixed(2)}% 가정.
      </p>

      <div className="three-systems">
        <div className="system-col danger">
          <h4>🚪 ① 검사를 아예 안 하면</h4>
          <p className="system-verdict">사기 {dailyFraud.toLocaleString()}건이 매일 그냥 통과</p>
          <ul className="plain">
            <li>사람 검토: 0건</li>
            <li>고객 불편: 없음</li>
            <li>
              <strong>💸 사기 피해: 전부 그대로 당함</strong>
            </li>
          </ul>
        </div>

        <div className="system-col warn">
          <h4>🚧 ② 의심되면 전부 막으면</h4>
          <p className="system-verdict">멀쩡한 고객까지 전부 줄 세움</p>
          <ul className="plain">
            <li>사람 검토: {daily.toLocaleString()}건 (물리적으로 불가능)</li>
            <li>
              <strong>😡 고객 불편: 최악 — 은행이 멈춘 것과 같음</strong>
            </li>
            <li>사기 피해: 없음 (대신 아무도 송금을 못 함)</li>
          </ul>
        </div>

        <div className="system-col good">
          <h4>🤖 ③ SafeFDS (실제 측정값)</h4>
          <p className="system-verdict">
            안전한 {(rule.approve_rate * 100).toFixed(0)}%는 자동 통과
          </p>
          <ul className="plain">
            <li>사람 검토: {dailyReviews.toLocaleString()}건</li>
            <li>고객 불편: 의심 거래만</li>
            <li>
              사기 {dailyFraud.toLocaleString()}건 중{" "}
              <strong>{caughtByRule.toLocaleString()}건 적발</strong> (잡은 비율{" "}
              {(rule.fraud_recall * 100).toFixed(1)}%)
            </li>
          </ul>
        </div>
      </div>

      <p className="muted">
        ①과 ②는 실제로 만든 시스템이 아니라 같은 데이터에서 계산해 본 비교값이에요. ③만 고정
        테스트셋에서 실제로 측정한 결과입니다.
      </p>
      <p className="callout">
        세게 막을수록 사기는 줄지만 멀쩡한 고객이 막히고, 약하게 막을수록 그 반대예요. 시소와
        같아서 <strong>양쪽을 동시에 다 가질 수는 없어요.</strong> 할 수 있는 건 정확히 재서
        &quot;어디쯤에 설지&quot;를 정하는 것 — SafeFDS가 하는 일이 바로 그 측정입니다.
      </p>
    </section>
  );
}
